import { ConfigModule } from 'shared/types'
import { controls } from 'shared/components/controls'

export interface RaceTheme {
	classColors: string[]
	nameColor: string
	scoreColor: string
	columnGutter: number
	gapColor: string
	markerHeight: number
	markerLineColor: string
	paceMarkerOutlineColor: string
	participantHighlightColor: string
	markerLineWidth: number
	eliminationLineWidth: number
	eliminationFlagColor: string
	eliminationFlagTextColor: string
	segmentMarkerStyle: string
	segmentLineWidth: number
	segmentLineColor: string
	showSegmentMarkerLabels: boolean
	moreColor: string
	finishedRacerBorderHeight: number
	hybridBorderHeight: number
	vitalPadding: number
	vitalWidths: {
		place: number, name: number, lane: number, affiliation: number, gap: number, pace: number
	},
	vitalTextShadow: boolean
	rowHeightSmScale: number,
	rowHeightLgScale: number
	towPaceColor: string
	towPace2Color: string
}

import './blob'
declare module './blob' {
	interface ConfigBlob {
		raceTheme: RaceTheme
	}
}

export const raceTheme: ConfigModule<RaceTheme> = {
	page: 'race',
	panel: 'Theme',
	isTheme: true,
	fields: {
		classColors: {
			default: [
			  '#FFF100', '#B1A0C7', '#87C540', '#F36E29', '#45B9FF', '#FF45E5',
			  '#73FFE1', '#C79E33', '#BEBFC1', '#FF5733', '#FF5733', '#33FFBD',
			  '#FF33A8', '#338AFF', '#B833FF', '#33FF57', '#FFA833', '#C833FF',
			  '#6A33FF', '#FF336A', '#33FFC8', '#FFA633', '#336AFF', '#B833FF',
			  '#33FF8A', '#FF33D8', '#FFC833', '#338AFF', '#FF33FF', '#33FFB8',
			  '#FF336A', '#338AFF', '#33AFFF', '#FF33B8', '#A833FF', '#33FF3A',
			  '#FF33C8', '#FFC833', '#FF33FF', '#33FFD8', '#FF3368', '#338AFF',
			  '#33AFFF', '#FF33B8', '#A833FF', '#33FF3A', '#FF33C8', '#FFC833',
			  '#FF33FF', '#33FFD8', '#FF3368', '#338AFF', '#33AFFF', '#FF33B8',
			  '#A833FF', '#33FF3A', '#FF33C8', '#FFC833', '#FF33FF', '#33FFD8',
			  '#FF3368', '#338AFF', '#33AFFF', '#FF33B8', '#A833FF', '#33FF3A',
			  '#FF33C8', '#FFC833', '#FF33FF', '#33FFD8', '#FF3368', '#338AFF',
			  '#33AFFF', '#FF33B8', '#A833FF', '#33FF3A', '#FF33C8', '#FFC833',
			  '#FF33FF', '#33FFD8', '#FF3368', '#338AFF', '#33AFFF', '#FF33B8',
			  '#A833FF', '#33FF3A', '#FF33C8', '#FFC833', '#FF33FF', '#33FFD8',
			  '#FF3368', '#338AFF', '#33AFFF'
			]
		  },
		nameColor: {
			default: 'white',
			control: controls.Color('Name Color')
		},
		scoreColor: {
			default: 'white',
			control: controls.Color('Score Color')
		},
		columnGutter: {
			default: 15,
			control: controls.Number('Column Gutter')
		},
		gapColor: {
			default: 'white',
			control: controls.Color('Gap Color')
		},
		markerHeight: {
			default: 0.0324,
			control: controls.Number('Marker Height %', 0.001, 0.0001, 1)
		},
		markerLineColor: {
			default: '#b4b4b4',
			control: controls.Color('Meter Line Color')
		},
		markerLineWidth: {
			default: 2,
			control: controls.Number('Marker Line Width')
		},
		eliminationLineWidth: {
			default: 8,
			subTab: 'Elimination',
			control: controls.Number('Elimination Line Width')
		},
		eliminationFlagColor: {
			default: 'white',
			subTab: 'Elimination',
			control: controls.Color('Elimination Flag Color')
		},
		eliminationFlagTextColor: {
			default: 'black',
			subTab: 'Elimination',
			control: controls.Color('Elimination Flag Text Color')
		},
		segmentMarkerStyle: {
			default: 'marker with line',
			subTab: 'Biathlon',
			control: controls.Radio('Segment Markers', {
				'Not Shown': 'not shown',
				'As Marker': 'marker',
				'As Marker with Line': 'marker with line',
				'As Circle': 'circle',
				'As Circle with Line': 'circle with line',
				'Line Only': 'line',
			}),
		},
		segmentLineWidth: {
			default: 6,
			subTab: 'Biathlon',
			control: controls.Number('Segment Line Width'),
		},
		segmentLineColor: {
			default: '#ff8800',
			subTab: 'Biathlon',
			control: controls.Color('Segment Line Color'),
		},
		showSegmentMarkerLabels: {
			default: true,
			subTab: 'Biathlon',
			control: controls.Checkbox('Segment Marker Labels'),
		},
		paceMarkerOutlineColor: {
			default: 'white',
		},
		participantHighlightColor: {
			default: 'yellow',
			control: controls.Color('Participant Highlight Color')
		},
		moreColor: {
			default: 'white'
		},
		finishedRacerBorderHeight: {
			default: 4
		},
		hybridBorderHeight: {
			default: 8
		},
		vitalPadding: {
			default: 30
		},
		vitalWidths: {
		  default: {
			place: 0.82,
			name: 4,
			lane: 1.3,
			affiliation: 2.5,
			gap: 1.73, // 2
			pace: 2.46
		  }
		},
		vitalTextShadow: {
			default: true,
			control: controls.Checkbox('Vitals Shadow')
		},
		rowHeightSmScale: {
			default: 0.55, // 0.4
			control: controls.Number('Row Small Scale', 0.05, 0.05, 0.55)
		},
		rowHeightLgScale: {
			default: 1,
			control: controls.Number('Row Large Scale', 0.55, 0.05, 1)
		},
		towPaceColor: {
			default: 'white',
			subTab: 'Erg Battle',
			control: controls.Color('Erg Battle Pace 1 Color')
		},
		towPace2Color: {
			default: 'black',
			subTab: 'Erg Battle',
			control: controls.Color('Erg Battle Pace 2 Color')
		},
	}
}
